/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 10 2024
 **/

import { atom, useAtom } from "jotai";
import styled from "@emotion/styled";
import { atomFamily } from "jotai/utils";

import { Column, Grid, Typography } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";

import theme from "@/styles/theme/theme";

const topBarColorAtom = atom<Record<string, string>>({});
export const topBarColorAtomFamily = atomFamily((spaceID: string) => {
	return atom(
		(get) => {
			return get(topBarColorAtom)[spaceID] ?? theme.light.colors.gradient_40_100_w80;
		},
		(_, set, arg: string) => {
			set(topBarColorAtom, (prev) => {
				return { ...prev, [spaceID]: arg };
			});
		}
	);
});

const TOP_BAR_COLOR_LIST = [
	theme.light.colors.gradient_40_100_w80,
	theme.light.colors.deco2_10_100,
	theme.light.colors.deco3_10_100,
	theme.light.colors.deco4_10_100,
	theme.light.colors.deco5_10_100,
	theme.light.colors.deco6_10_100,
	theme.light.colors.deco7_10_100,
	theme.light.colors.deco8_10_100,
	theme.light.colors.deco9_10_100,
	theme.light.colors.deco11_10_100,
	theme.light.colors.deco12_10_100,
	theme.light.colors.deco13_10_100,
	theme.light.colors.deco14_10_100,
	theme.light.colors.deco16_10_100
];

export default function TopBarColor({ spaceID }: { spaceID: string }) {
	const [topBarColor, setTopBarColor] = useAtom(topBarColorAtomFamily(spaceID));

	const handleChange = (color: string) => {
		setTopBarColor(color);
	};

	return (
		<Column css={{ padding: "0 8px 4px 8px" }} gap={4}>
			<Typography typoColor="neutral_60_100" typoSize="Body5">
				{_T("상단 바 컬러")}
			</Typography>
			<Grid.ul row={2} column={7} gridGap={4}>
				{TOP_BAR_COLOR_LIST.map((color) => (
					<ColorItem
						key={color}
						color={color}
						selected={color === topBarColor}
						onClick={() => {
							handleChange(color);
						}}
					/>
				))}
			</Grid.ul>
		</Column>
	);
}

const ColorItem = styled.li<{ color: string; selected: boolean }>`
	background: ${({ color }) => color};
	width: 24px;
	height: 24px;
	border-radius: 50%;
	border: 1px solid ${({ theme, selected, color }) => (selected ? theme.colors.neutral_90_100 : color)};
	cursor: pointer;
	&:hover {
		border: 1px solid ${({ theme }) => theme.colors.neutral_80_50};
	}
`;
